import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supportAPI } from '../../services';
import {
  LifeBuoy,
  Plus,
  X,
  Upload,
  Search,
  Paperclip,
  Star
} from 'lucide-react';

const INTERNAL_CATEGORIES = ['IT & Hardware', 'HR Policy', 'Payroll Issue', 'Leave & Attendance', 'Access & Permissions', 'Facilities', 'Other'];
const PLATFORM_CATEGORIES = ['Payroll Module', 'Leave Module', 'Attendance / Biometric', 'Statutory / Compliance', 'Reports & Analytics', 'User Management', 'Integration / API', 'Other'];

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800'
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  pending: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800'
};

const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', pending: 'Pending', resolved: 'Resolved', closed: 'Closed' };
const NEXT_ALLOWED = { open: ['in_progress', 'closed'], in_progress: ['pending', 'resolved', 'closed'], pending: ['in_progress', 'closed'], resolved: ['closed'], closed: [] };
const HR_ROLES = ['admin', 'hod', 'hr_head'];

const EMPTY_FORM = { subject: '', category: '', priority: 'medium', description: '' };

const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const SupportCentre = () => {
  const { user } = useAuth();
  const isHrRole = HR_ROLES.includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canSeeAllInternal = isHrRole || isManager;

  const [activeTab, setActiveTab] = useState('internal');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({ status: '', priority: '', category: '', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentInternal, setCommentInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [assignableAgents, setAssignableAgents] = useState([]);
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  const group = activeTab === 'internal' ? supportAPI.internal : supportAPI.platform;
  const categories = activeTab === 'internal' ? INTERNAL_CATEGORIES : PLATFORM_CATEGORIES;
  const canSeeAll = activeTab === 'internal' ? canSeeAllInternal : isAdmin;
  // Only DigiHR Support (superadmin) can resolve/assign platform tickets; on the client side, an
  // HR Admin may only close a ticket once it's been marked Resolved.
  const canAssign = activeTab === 'internal' ? isAdmin : false;
  // Who may move an internal ticket's status forward: HR roles (unchanged), or whoever it's
  // currently assigned to - assignment is no longer limited to HR staff (see EmployeeFormPage's
  // "Handle Support Tickets" permission).
  const canModerateSelected = !!selected && activeTab === 'internal' &&
    (isHrRole || selected.assignee?._id === (user._id || user.id));

  useEffect(() => {
    if (!isAdmin) return;
    supportAPI.internal.getAssignableAgents()
      .then((res) => setAssignableAgents(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setAssignableAgents([]));
  }, [isAdmin]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        category: filters.category || undefined,
        search: filters.search || undefined
      };
      const res = canSeeAll ? await group.getAll(params) : await group.getMine();
      setTickets(Array.isArray(res?.data) ? res.data : []);
    } catch {
      showToast('Failed to load tickets', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, canSeeAll, group]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.category || !form.description.trim()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }
    try {
      setSubmitting(true);
      await group.create(form);
      showToast('Ticket raised successfully');
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      showToast(err?.message || 'Failed to raise ticket', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await group.getOne(id);
      setSelected(res.data);
    } catch {
      showToast('Failed to load ticket', 'error');
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setCommentText('');
    setCommentInternal(false);
    setFiles([]);
    setRatingScore(0);
    setRatingComment('');
  };

  const refreshSelected = async () => {
    const res = await group.getOne(selected._id);
    setSelected(res.data);
  };

  const handleStatusChange = async (status) => {
    try {
      const res = await group.update(selected._id, { status });
      setSelected(res.data);
      showToast('Status updated');
      load();
    } catch (err) {
      showToast(err?.message || 'Failed to update status', 'error');
    }
  };

  const handleAssigneeChange = async (assigneeId) => {
    try {
      const res = await group.update(selected._id, { assignee: assigneeId || null });
      setSelected(res.data);
      showToast(assigneeId ? 'Ticket assigned' : 'Ticket unassigned');
      load();
    } catch (err) {
      showToast(err?.message || 'Failed to assign ticket', 'error');
    }
  };

  const handleSubmitRating = async () => {
    if (!ratingScore) {
      showToast('Please select a rating', 'error');
      return;
    }
    try {
      await group.rateTicket(selected._id, { score: ratingScore, comment: ratingComment.trim() });
      showToast('Rating submitted');
      await refreshSelected();
    } catch (err) {
      showToast(err?.message || 'Failed to submit rating', 'error');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      await group.addComment(selected._id, { text: commentText.trim(), is_internal: commentInternal });
      setCommentText('');
      setCommentInternal(false);
      await refreshSelected();
    } catch (err) {
      showToast(err?.message || 'Failed to add comment', 'error');
    }
  };

  const handleUploadFiles = async () => {
    if (files.length === 0) return;
    try {
      await group.uploadAttachments(selected._id, files);
      setFiles([]);
      await refreshSelected();
      showToast('Files uploaded');
    } catch (err) {
      showToast(err?.message || 'Failed to upload files', 'error');
    }
  };

  const slaProgress = (ticket) => {
    const created = new Date(ticket.createdAt).getTime();
    const due = new Date(ticket.sla_due_at).getTime();
    const now = Date.now();
    const pct = Math.min(100, Math.max(0, ((now - created) / (due - created)) * 100));
    const breached = now > due && !['resolved', 'closed'].includes(ticket.status);
    return { pct, breached };
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-blue-600" /> Support Centre
          </h1>
          <p className="text-sm text-gray-500 mt-1">Raise and track HR/IT tickets and DigiHR platform issues</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setForm(EMPTY_FORM); }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New Ticket
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('internal')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'internal' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Internal HR Support
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('platform')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'platform' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              DigiHR Platform Issues
            </button>
          )}
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3">
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
        <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search subject/description..."
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Ticket list */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : tickets.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <LifeBuoy className="w-12 h-12 mx-auto mb-3 opacity-30" />
            No tickets found
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SLA</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tickets.map((t) => {
                const sla = slaProgress(t);
                return (
                  <tr key={t._id} onClick={() => openDetail(t._id)} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{t.ticket_id}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 max-w-xs truncate">{t.subject}</td>
                    <td className="px-4 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span></td>
                    <td className="px-4 py-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span></td>
                    <td className="px-4 py-4 w-32">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full transition-all ${sla.breached ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${sla.pct}%` }} />
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">{formatDateTime(t.updatedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                Raise {activeTab === 'internal' ? 'Internal HR' : 'DigiHR Platform'} Ticket
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  maxLength={255}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority <span className="text-red-500">*</span></label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Slide-out detail panel */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={closeDetail} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl h-full overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selected.ticket_id}</h2>
                <p className="text-sm text-gray-500">{selected.subject}</p>
              </div>
              <button onClick={closeDetail} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${PRIORITY_COLORS[selected.priority]}`}>{selected.priority}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selected.status]}`}>{STATUS_LABELS[selected.status]}</span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{selected.category}</span>
              </div>

              {(() => {
                const sla = slaProgress(selected);
                return (
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>SLA</span>
                      <span>{sla.breached ? 'Breached' : `Due ${formatDateTime(selected.sla_due_at)}`}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${sla.breached ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${sla.pct}%` }} />
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg mt-1 text-sm">{selected.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="text-xs font-medium text-gray-500">Raised By</label>
                  <p className="text-gray-900">{selected.raised_by?.name || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Assignee</label>
                  <p className="text-gray-900">{selected.assignee?.name || 'Unassigned'}</p>
                </div>
              </div>

              {/* Status / assign controls */}
              {(canModerateSelected || canAssign) && selected.status !== 'closed' && (
                <div className="pt-3 border-t border-gray-100 space-y-3">
                  {canModerateSelected && NEXT_ALLOWED[selected.status].length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 block mb-2">Update Status</label>
                      <div className="flex gap-2 flex-wrap">
                        {NEXT_ALLOWED[selected.status].map((s) => (
                          <button key={s} onClick={() => handleStatusChange(s)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700">
                            {STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* A client HR Admin may only close a platform ticket once DigiHR has marked it Resolved */}
                  {!canModerateSelected && activeTab === 'platform' && selected.status === 'resolved' && (
                    <button onClick={() => handleStatusChange('closed')} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700">
                      Confirm & Close
                    </button>
                  )}
                  {canAssign && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 block mb-2">Assignee</label>
                      <select
                        value={selected.assignee?._id || ''}
                        onChange={(e) => handleAssigneeChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">Unassigned</option>
                        {assignableAgents.map((a) => (
                          <option key={a._id} value={a._id}>
                            {a.name}{a.designation ? ` (${a.designation})` : HR_ROLES.includes(a.role) ? ` (${a.role})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Rating of how the ticket was handled, once resolved/closed - HR rates internal
                  tickets; the client admin who raised it rates DigiHR's platform-ticket resolution */}
              {((activeTab === 'internal' && isHrRole) || (activeTab === 'platform' && isAdmin)) && ['resolved', 'closed'].includes(selected.status) && (
                <div className="pt-3 border-t border-gray-100">
                  <label className="text-xs font-medium text-gray-500 block mb-2">Resolution Rating</label>
                  {selected.rating?.score ? (
                    <div>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-5 h-5 ${n <= selected.rating.score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      {selected.rating.comment && <p className="text-sm text-gray-600 mt-1">{selected.rating.comment}</p>}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button" onClick={() => setRatingScore(n)}>
                            <Star className={`w-6 h-6 ${n <= ratingScore ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={ratingComment}
                        onChange={(e) => setRatingComment(e.target.value)}
                        placeholder="Optional comment..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <button onClick={handleSubmitRating} disabled={!ratingScore} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                        Submit Rating
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Attachments */}
              <div className="pt-3 border-t border-gray-100">
                <label className="text-xs font-medium text-gray-500 block mb-2">Attachments ({selected.attachments?.length || 0}/5)</label>
                <div className="space-y-1 mb-2">
                  {(selected.attachments || []).map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                      <Paperclip className="w-3.5 h-3.5" /> {a.filename}
                    </a>
                  ))}
                </div>
                {(selected.attachments?.length || 0) < 5 && (
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-500 flex-1">
                      <Upload className="w-4 h-4 text-gray-400" />
                      {files.length > 0 ? `${files.length} file(s) selected` : 'Choose files'}
                      <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        className="hidden"
                        onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5 - (selected.attachments?.length || 0)))}
                      />
                    </label>
                    <button onClick={handleUploadFiles} disabled={files.length === 0} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50">
                      Upload
                    </button>
                  </div>
                )}
              </div>

              {/* Comments */}
              <div className="pt-3 border-t border-gray-100">
                <label className="text-xs font-medium text-gray-500 block mb-2">Comments</label>
                <div className="space-y-3 mb-3 max-h-64 overflow-y-auto">
                  {(selected.comments || []).length === 0 ? (
                    <p className="text-sm text-gray-400">No comments yet</p>
                  ) : selected.comments.map((c) => (
                    <div key={c._id} className={`p-3 rounded-lg text-sm ${c.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{c.author_id?.name || c.role_label}</span>
                        <span className="text-xs text-gray-400">{formatDateTime(c.created_at)}</span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{c.text}</p>
                      {c.is_internal && <span className="text-xs text-yellow-700 font-medium">Internal note</span>}
                    </div>
                  ))}
                </div>
                {selected.status !== 'closed' && (
                  <div className="space-y-2">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      rows={2}
                      placeholder="Add a comment..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex items-center justify-between">
                      {activeTab === 'internal' && isHrRole ? (
                        <label className="flex items-center gap-2 text-xs text-gray-600">
                          <input type="checkbox" checked={commentInternal} onChange={(e) => setCommentInternal(e.target.checked)} />
                          Internal note (not visible to requester)
                        </label>
                      ) : <span />}
                      <button onClick={handleAddComment} disabled={!commentText.trim()} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                        Post
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportCentre;
